import type {
  AdminRef,
  ClosurePreview,
  RefundLeg,
} from 'src/pages/admin/services/vaultOperationsService';
import { parseApiError } from 'src/pages/admin/utils/decimal-input';

import { OPERATIONS_ENGINE_ERROR_KEYS, formatUnits, formatUsdc } from '../vaults/helpers';
import { OPERATIONS_NS as NS, RECOVERED_PATTERN, TEXT_MAX } from './constants';

export { formatInstant, middle } from '../vaults/helpers';
export { newRequestId } from '../reconciliation/helpers';

type Translate = (key: string, options?: object) => string;

const EMPTY = '—';

/** Rule 4: money in a confirmation is the BFF's full 6 dp, with thousands separators; "—" for a figure the BFF did not send. */
export const money6 = (value: string | null | undefined): string => {
  if (value == null) return EMPTY;
  // a signed figure (a close-out's slippage can be a gain): the sign is kept, the digits are formatted as ever
  const v = value.trim();
  if (v.startsWith('-')) {
    const rest = formatUnits(v.slice(1));
    return rest === EMPTY ? EMPTY : `-${rest}`;
  }
  return formatUnits(v);
};

/** Rule 4: 2 dp in tables and on cards. */
export const money2 = (value: string | null | undefined): string =>
  value == null ? EMPTY : formatUsdc(value);

/** A name, never a raw admin id: the BFF's resolved name and role, or "Unknown admin (id…)". */
export const adminName = (ref: AdminRef | undefined, id: string | null | undefined): string => {
  // The system actor is already named “Automatic (system)”: no second role in brackets.
  if (ref)
    return ref.role && ref.known && ref.role !== 'system' ? `${ref.name} (${ref.role})` : ref.name;
  if (!id) return EMPTY;
  return `Unknown admin (${id.slice(0, 8)}…)`;
};

/** Whole minutes between `now` and `iso` (positive = in the future); null for a malformed instant. */
export const minutesUntil = (iso: string | null | undefined, now: number): number | null => {
  if (!iso) return null;
  const at = Date.parse(iso);
  return Number.isNaN(at) ? null : Math.trunc((at - now) / 60_000);
};

/** "2 h 10 min", "45 min", "less than a minute" parts for a duration in minutes (absolute value). */
export const durationParts = (minutes: number): { hours: number; minutes: number } => {
  const total = Math.abs(minutes);
  return { hours: Math.trunc(total / 60), minutes: total % 60 };
};

export const durationText = (minutes: number, translate: Translate): string => {
  const { hours, minutes: m } = durationParts(minutes);
  if (hours === 0 && m === 0) return translate(`${NS}.time.lessThanMinute`);
  if (hours === 0) return translate(`${NS}.time.minutes`, { minutes: m });
  if (m === 0) return translate(`${NS}.time.hours`, { hours });
  return translate(`${NS}.time.hoursMinutes`, { hours, minutes: m });
};

const text = (value: string): number => Array.from(value.trim()).length;

/** A required 1–TEXT_MAX text, counted as the BFF counts it (trimmed, code points). Returns the i18n key of the problem. */
export const requiredText = (value: string): string | undefined => {
  const n = text(value);
  if (n === 0) return `${NS}.errors.required`;
  return n > TEXT_MAX ? `${NS}.errors.tooLong` : undefined;
};

export const validateRecovered = (value: string): string | undefined =>
  RECOVERED_PATTERN.test(value.trim()) ? undefined : `${NS}.errors.recoveredFormat`;

/**
 * "Part closed: __ %" as the fraction the BFF expects, by moving the decimal point on the string (never a float):
 * "98.5" → "0.985", "100" → "1", "0" → "0" (the BFF accepts 0: nothing closed). At most 4 decimals (the fraction has 6); null when it is not 0 ≤ % ≤ 100.
 */
export const percentToFraction = (percent: string): string | null => {
  const m = /^(\d{1,3})(?:\.(\d{1,4}))?$/.exec(percent.trim());
  if (!m) return null;
  const digits = `${m[1]}${(m[2] ?? '').padEnd(4, '0')}`.replace(/^0+(?=\d)/, '').padStart(5, '0');
  if (Number(digits) > 1_000_000) return null;
  if (/^0+$/.test(digits)) return '0';
  const int = digits.slice(0, -6) || '0';
  const frac = digits.padStart(6, '0').slice(-6).replace(/0+$/, '');
  return frac ? `${int}.${frac}` : int;
};

/** The inverse, for prefilling "Part closed": "0.985000" → "98.5", "0.5" → "50". */
export const fractionToPercent = (fraction: string | null | undefined): string => {
  const m = /^(\d+)(?:\.(\d+))?$/.exec((fraction ?? '').trim());
  if (!m) return '';
  const all = `${m[1]}${(m[2] ?? '').padEnd(6, '0').slice(0, 6)}`;
  const padded = all.replace(/^0+(?=\d)/, '').padStart(5, '0');
  const int = padded.slice(0, -4).replace(/^0+(?=\d)/, '') || '0';
  const frac = padded.slice(-4).replace(/0+$/, '');
  return frac ? `${int}.${frac}` : int;
};

/** The close-out figure of the dialog: "Everything closed" is exactly 1, "Part closed" converts the percent. */
export const closedFractionOf = (values: Record<string, string>): string | null =>
  values.closeMode === 'FULL' ? '1' : percentToFraction(values.closedPercent ?? '');

/** The browser's offset as the column header says it: "UTC+7", "UTC-3", "UTC+5:30", "UTC". */
export const utcZoneLabel = (date: Date = new Date()): string => {
  const offset = -date.getTimezoneOffset();
  if (offset === 0) return 'UTC';
  const abs = Math.abs(offset);
  const minutes = abs % 60;
  return `UTC${offset < 0 ? '-' : '+'}${Math.trunc(abs / 60)}${minutes ? `:${String(minutes).padStart(2, '0')}` : ''}`;
};

/** Whether the system itself did it (the engine's own automatic refunds: proposedBy `system:auto`). */
export const doneBySystem = (row: {
  proposedBy: string;
  proposedByAdmin?: { role: string | null };
}): boolean => row.proposedByAdmin?.role === 'system' || /^system:/i.test(row.proposedBy);

/**
 * A − B = C as the card prints it: 2 dp when the rounded-down figures add up, else the full 6 dp, so the line that explains a
 * difference never contradicts itself (1,204.50 − 4.50 ≠ 1,199.99).
 */
export const sumLine = (
  a: string | null | undefined,
  b: string | null | undefined,
  c: string | null | undefined
): [string, string, string] => {
  const cents = (v: string | null | undefined) => {
    const m = /^(\d[\d,]*)\.(\d{2})$/.exec(money2(v));
    return m ? BigInt(`${m[1].replace(/,/g, '')}${m[2]}`) : null;
  };
  const x = cents(a);
  const y = cents(b);
  const z = cents(c);
  return x !== null && y !== null && z !== null && x - y === z
    ? [money2(a), money2(b), money2(c)]
    : [money6(a), money6(b), money6(c)];
};

/** XBIT-5159: the sum of USDC decimal strings as a 6-dp string, added as integers; what is not a decimal is skipped. */
export const sumUsdc = (values: string[]): string => {
  const micros = values.reduce((total, value) => {
    const m = /^(\d+)(?:\.(\d{1,6}))?$/.exec(value.trim());
    return m ? total + BigInt(`${m[1]}${(m[2] ?? '').padEnd(6, '0')}`) : total;
  }, BigInt(0));
  const digits = micros.toString().padStart(7, '0');
  return `${digits.slice(0, -6)}.${digits.slice(-6)}`;
};

/** Numeric string equality without a float: "1" = "1.000000", "1204.5" = "1204.500000". */
export const sameDecimal = (
  a: string | null | undefined,
  b: string | null | undefined
): boolean => {
  if (a == null || b == null) return false;
  const norm = (v: string) => {
    const m = /^(\d+)(?:\.(\d+))?$/.exec(v.trim());
    if (!m) return null;
    return `${m[1].replace(/^0+(?=\d)/, '')}.${(m[2] ?? '').replace(/0+$/, '')}`;
  };
  const x = norm(a);
  return x !== null && x === norm(b);
};

/** The figures an approval of a close-out books, in the order the confirmation lists them; null when none were previewed. */
export const bookingFigures = (
  preview: ClosurePreview | null | undefined
): { paid: string; fee: string; net: string; slippage: string } | null =>
  preview && preview.paid != null && preview.fee != null && preview.net != null
    ? {
        paid: money6(preview.paid),
        fee: money6(preview.fee),
        net: money6(preview.net),
        slippage: money6(preview.slippage),
      }
    : null;

/** Both legs of a refund as the confirmation names them (rule 2: amount, from, to). */
export const refundLegs = (
  refund: { vaultToCold: RefundLeg; toSender: RefundLeg; fee: string | null } | null,
  /** 6 dp inside a confirmation (rule 4), 2 dp on a card. */
  fmt: (value: string | null | undefined) => string = money6
) =>
  refund
    ? {
        vaultToCold: { ...refund.vaultToCold, amount: fmt(refund.vaultToCold.amount) },
        toSender: { ...refund.toSender, amount: fmt(refund.toSender.amount) },
        fee: fmt(refund.fee),
      }
    : null;

const ERROR_KEYS: Record<string, string> = {
  STEP_UP_REQUIRED: 'stepUp',
  STEP_UP_UNAVAILABLE: 'stepUpUnavailable',
  VAULT_WORKORDER_SAME_APPROVER: 'sameApprover',
  VAULT_CLOSURE_PROPOSAL_SAME_APPROVER: 'sameApprover',
  VAULT_QUARANTINE_SAME_APPROVER: 'sameApprover',
  VAULT_WORKORDER_NOT_AWAITING: 'notAwaiting',
  VAULT_CLOSURE_PROPOSAL_NOT_AWAITING: 'notAwaiting',
  VAULT_QUARANTINE_NOT_AWAITING: 'notAwaiting',
  VAULT_WORKORDER_NOT_FOUND: 'notFound',
  VAULT_CLOSURE_RESOLUTION_NOT_FOUND: 'notFound',
  VAULT_CLOSURE_PROPOSAL_NOT_FOUND: 'notFound',
  VAULT_QUARANTINE_NOT_FOUND: 'notFound',
  VAULT_WORKORDER_NOT_UNRESOLVED: 'notUnresolved',
  VAULT_WORKORDER_ALREADY_PROPOSED: 'alreadyProposed',
  VAULT_WORKORDER_KEY_CONFLICT: 'keyConflict',
  VAULT_CLOSURE_PROPOSAL_KEY_CONFLICT: 'keyConflict',
  VAULT_QUARANTINE_REQUEST_CONFLICT: 'keyConflict',
  VAULT_WORKORDER_FAIL_TOO_EARLY: 'failTooEarly',
  VAULT_WORKORDER_CONFIRM_REFUSED: 'evidenceRefused',
  VAULT_WORKORDER_FAIL_REFUSED: 'evidenceRefused',
  VAULT_WORKORDER_POSSIBLE_PAYMENT: 'possiblePayment',
  VAULT_WORKORDER_EVIDENCE_UNAVAILABLE: 'evidenceUnavailable',
  VAULT_CLOSURE_RESOLUTION_REFUSED: 'closureRefused',
  VAULT_CLOSURE_RECOVERY_INVALID: 'closureRecoveryInvalid',
  VAULT_QUARANTINE_NOT_REFUNDABLE: 'notRefundable',
  VAULT_QUARANTINE_FEE_CHANGED: 'feeChanged',
  VAULT_QUARANTINE_FEE_NOT_BELOW_BASIS: 'feeNotBelowBasis',
  VAULT_QUARANTINE_PARAMS_UNREADABLE: 'unavailable',
  VAULT_QUEUE_TRUNCATED: 'queueTruncated',
  VAULT_QUEUE_CHANGED: 'queueChanged',
  VAULT_LANE_RESUME_SAME_APPROVER: 'laneSameApprover',
  VAULT_LANE_NOT_PAUSED: 'laneNotPaused',
  VAULT_LANE_RESUME_ALREADY_PROPOSED: 'laneResumeAlreadyProposed',
  VAULT_LANE_RESUME_NOT_PROPOSED: 'laneResumeNotProposed',
  VAULT_LANE_PAUSE_INCOMPLETE: 'lanePauseIncomplete',
  VAULT_LANE_PAUSE_SCOPE_UNKNOWN: 'lanePauseScopeUnknown',
  VAULT_LANE_PAUSE_UNAVAILABLE: 'lanePauseUnavailable',
};

const INVALID_CODES = new Set([
  'VAULT_WORKORDER_INVALID',
  'VAULT_CLOSURE_PROPOSAL_INVALID',
  'VAULT_QUARANTINE_INVALID',
]);

/** A refused read or action, in the console's words; the engine's own text is shown only for a refused input. */
export const operationsErrorMessages = (error: unknown, translate: Translate): string[] => {
  const info = parseApiError(error);
  const err = (key: string, options?: object) => translate(`${NS}.errors.${key}`, options);
  if (info.code && INVALID_CODES.has(info.code)) {
    return [err('invalid', { message: info.messages[0] ?? '' })];
  }
  // A DTO validation 400 carries no code, only the validator's text.
  if (info.status === 400 && !info.code && info.messages.length > 0) {
    return [err('invalid', { message: info.messages.join('; ') })];
  }
  const known = info.code ? ERROR_KEYS[info.code] : undefined;
  if (known) return [err(known)];
  if (info.status === 403) return [err('forbidden')];
  const engine = info.code ? OPERATIONS_ENGINE_ERROR_KEYS[info.code] : undefined;
  if (engine) return [err(engine)];
  // A bare 504 (an ingress in front of the BFF) may still have applied too.
  return [err(info.status === 504 ? 'timeout' : 'failed')];
};
